import { Children, type CSSProperties, useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactElement, type TouchEvent } from 'react'
import { type CarouselProps } from './carousel.props'
import * as S from './carousel.styled'
import useDimensions from '../../hooks/useDimentions'

export function Carousel ({
  arrowImg,
  autoPlay = false,
  children,
  className = '',
  inactiveButtonColor = '',
  interval = 3000,
  offset = 0,
  showArrows = false,
  showDots = false
}: CarouselProps): ReactElement {
  const { isDesktop } = useDimensions()
  const [clickBlocked, setClickBlocked] = useState(false)
  const [currentSlide, setCurrentSlide] = useState(0)
  const [focus, setFocus] = useState(false)
  const [mouseLeft, setMouseLeft] = useState(false)
  const mouseStartX = useRef<number | null>(null)
  const mouseEndX = useRef<number | null>(null)
  const touchStartX = useRef<number | null>(null)
  const touchEndX = useRef<number | null>(null)
  const totalSlides = Children.count(children)

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index >= 0 ? index % totalSlides : totalSlides - 1)
  }, [totalSlides])

  const handleMouseEnter = useCallback(() => {
    setFocus(true)
    setMouseLeft(false)
  }, [])

  const handleMouseDown = useCallback((evt: MouseEvent<HTMLDivElement>) => {
    mouseStartX.current = evt.clientX
  }, [])

  const handleMouseMove = useCallback((evt: MouseEvent<HTMLDivElement>) => {
    if (mouseStartX.current !== null) {
      setClickBlocked(true)
      mouseEndX.current = evt.clientX
    }
  }, [])

  const handleMouseUp = useCallback(() => {
    setFocus(false)
    if (mouseStartX.current === null || mouseEndX.current === null) return

    const mouseDifference = mouseStartX.current - mouseEndX.current
    if (mouseDifference > 50) {
      goToSlide(currentSlide + 1)
    } else if (mouseDifference < -50) {
      goToSlide(currentSlide - 1)
    }

    mouseStartX.current = null
    mouseEndX.current = null
    setClickBlocked(false)
    if (!mouseLeft) setFocus(true)
  }, [currentSlide, goToSlide, mouseLeft])

  const handleMouseLeave = useCallback(() => {
    setFocus(false)
    setMouseLeft(true)
  }, [])

  const handleTouchStart = useCallback((evt: TouchEvent<HTMLDivElement>) => {
    touchStartX.current = evt.touches[0].clientX
  }, [])

  const handleTouchMove = useCallback((evt: TouchEvent<HTMLDivElement>) => {
    touchEndX.current = evt.touches[0].clientX
  }, [])

  const handleTouchEnd = useCallback(() => {
    if (touchStartX.current === null || touchEndX.current === null) return

    const touchDifference = touchStartX.current - touchEndX.current

    if (touchDifference > 50) {
      goToSlide(currentSlide + 1)
    } else if (touchDifference < -50) {
      goToSlide(currentSlide - 1)
    }

    touchStartX.current = null
    touchEndX.current = null
  }, [currentSlide, goToSlide])

  const transform = useMemo(() => {
    const baseTranslate = -currentSlide * 100
    const gap = 16
    if (isDesktop) return `translateX(${baseTranslate}%)`
    return `translateX(calc(${baseTranslate}% - ${currentSlide * gap}px + ${currentSlide * (offset * 2)}px + ${offset}px))`
  }, [currentSlide, isDesktop, offset])

  const minWidth = useMemo(() => {
    if (isDesktop) return '100%'
    return `calc(100% - ${offset * 2}px)`
  }, [isDesktop, offset])

  const maxWidth = useMemo(() => {
    if (totalSlides === 1) return minWidth
  }, [totalSlides, minWidth])

  const justifyContent = useMemo(() => {
    if (totalSlides === 1) return 'center'
  }, [totalSlides])

  const dotStyle = useCallback((index: number): CSSProperties => {
    return {
      backgroundColor: index !== currentSlide ? inactiveButtonColor : '',
      border: `1px solid ${inactiveButtonColor};`
    }
  }, [currentSlide, inactiveButtonColor])

  useEffect(() => {
    if (autoPlay && !focus) {
      const autoSlide = setInterval(() => {
        setCurrentSlide((prev) => ++prev % totalSlides)
      }, interval)

      return () => { clearInterval(autoSlide) }
    }
  }, [autoPlay, focus, interval, totalSlides])

  return <S.Container className={className}>
    <S.Carousel
      onMouseEnter={handleMouseEnter}
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onTouchStart={handleTouchStart}
    >
      <div className="slides" style={{ justifyContent, maxWidth, transform }}>
        {Children.map(children, (child, index) => (
          <div
            className={`slide ${clickBlocked ? 'click-blocked' : ''}`}
            key={index}
            style={{ minWidth }}
          >
            {child}
          </div>
        ))}
      </div>
    </S.Carousel>
    {(showDots && totalSlides > 1) && <div className="dots">
      {Array.from({ length: totalSlides }).map((_, index) => <button
        key={index}
        className={`dot ${index === currentSlide ? 'active' : ''}`}
        onClick={() => { goToSlide(index) }}
        style={dotStyle(index)}
      />)}
    </div>}
    {(showArrows && totalSlides > 1) && <button className="arrow previous" onClick={() => { goToSlide(currentSlide - 1) }} style={{ backgroundColor: inactiveButtonColor }}>
      <img src={arrowImg} alt="Slide anterior" />
    </button>}
    {(showArrows && totalSlides > 1) && <button className="arrow next" onClick={() => { goToSlide(currentSlide + 1) }} style={{ backgroundColor: inactiveButtonColor }}>
      <img src={arrowImg} alt="Próximo slide" />
    </button>}
  </S.Container>
}
